import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  CircularProgress,
  Typography,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import theme from "../../utils/theme";
import { formatText, getTimeSincePastDate } from "../../utils/utilityFunctions";
import { toast } from "react-hot-toast";
import AppButton from "../AppButton";
import { purposeList } from "../../utils/constant";

const styles = {
  collapsedAnalyticTableHeight: {
    height: "100%",
    maxHeight: 480,
    minHeight: 450,
    overflowY: "auto",
  },
  expandedAnalyticTableHeight: {
    height: "100%",
    maxHeight: 260,
    minHeight: 200,
    overflowY: "auto",
  },
  tableHeaderCell: {
    color: theme.palette.text.primary,
    fontWeight: 800,
    backgroundColor: theme.palette.background.dark,
    zIndex: 2,
    cursor: "pointer",
    userSelect: "none",
    maxWidth: { xs: 90, sm: 120, md: 180 },
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    p: { xs: 1, sm: 1.5 },
  },
  statusBadge: {
    px: 1,
    py: 0.5,
    borderRadius: 2,
    color: theme.palette.text.light,
    fontWeight: 700,
    fontSize: 13,
    display: "inline-block",
    minWidth: 100,
    textAlign: "center",
  },
  tableRow: {
    backgroundColor: theme.palette.background.dark,
    zIndex: 2,
  },
  headerSpan: {
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "bottom",
    color: theme.palette.text.light,
  },
  tableSortLabel: {
    color: theme.palette.text.light,
    "&.Mui-active": { color: theme.palette.text.light },
    "&:hover": { color: theme.palette.text.light },
    "& .MuiTableSortLabel-icon": {
      color: theme.palette.text.light + " !important",
    },
    p: 0,
    m: 0,
    minWidth: 0,
  },
  tableCellXLarge: {
    minWidth: "250px",
  },
  tableCellLarge: {
    minWidth: "180px",
  },
  tableCellMedium: {
    minWidth: "140px",
  },
  normalCellSize: {
    minWidth: "80px",
    width: "max-content",
  },
  bodyRow: (idx) => ({
    bgcolor:
      idx % 2 === 0
        ? theme.palette.background.paper
        : theme.palette.background.default,
    cursor: "pointer",
  }),
};

const ProjectTable = ({
  tableData,
  columns,
  showAnalytics,
  sortBy,
  sortDirection,
  handleSort,
  loading,
  handleAssignTemplate,
  assignLoader,
}) => {
  const navigate = useNavigate();

  // Navigate to View RFI only if both templates are created
  const handleRowClick = (row) => {
    console.log(row);
    const componentTemplate = Boolean(row?.component_template_assigned);
    const paintingTemplate = Boolean(row?.painting_checklist_template_assigned);

    if (
      componentTemplate &&
      paintingTemplate &&
      row?.purpose_type === purposeList.dimensional
    ) {
      navigate(`/viewRFI/${row.rfi_id}`);
      return;
    } else if (row?.purpose_type !== purposeList.dimensional) {
      navigate(`/viewRFI/${row.rfi_id}`);
      return;
    }

    // Build appropriate message
    let message = "";
    if (!componentTemplate && !paintingTemplate) {
      message =
        "Must have component templates and painting checklist templates to proceed.";
    } else if (!componentTemplate) {
      message = "Must have component templates to proceed.";
    } else if (!paintingTemplate) {
      message = "Must have painting checklist templates to proceed.";
    }
    toast.error(message);
  };

  const renderCell = (col, row) => {
    const cellSizeStyle =
      col?.cellSize === "large"
        ? styles.tableCellLarge
        : col?.cellSize === "medium"
        ? styles.tableCellMedium
        : col?.cellSize === "xlarge"
        ? styles.tableCellXLarge
        : styles.normalCellSize;

    const textType = col.fontWeight === "bold" && { fontWeight: 600 };

    const finalStyles = { ...cellSizeStyle, ...textType };

    switch (col.fieldType) {
      case "text":
        return (
          <TableCell sx={finalStyles}>
            {row[col.id] ? row[col.id] : "-----"}
          </TableCell>
        );
      case "date":
        return (
          <TableCell>
            {row[col.id] ? new Date(row[col.id]).toLocaleDateString() : ""}
          </TableCell>
        );
      case "number":
        return <TableCell>{row[col.id]}</TableCell>;
      case "progress_badge":
        return (
          <TableCell>
            <Box
              sx={{
                ...styles.statusBadge,
                ...finalStyles,
                bgcolor:
                  row[col.id].toLowerCase() === "open"
                    ? theme.palette.status.open
                    : row[col.id].toLowerCase() === "in_progress"
                    ? theme.palette.status.inProgress
                    : row[col.id].toLowerCase() === "closed"
                    ? theme.palette.status.closed
                    : theme.palette.status.completed,
              }}
            >
              {row[col.id] && formatText(row[col.id])}
            </Box>
          </TableCell>
        );
      case "status_badge":
        return (
          <TableCell>
            <Box
              sx={{
                ...styles.statusBadge,
                ...finalStyles,
                bgcolor:
                  row[col.id] === "Partially Uploaded"
                    ? theme.palette.warning.main
                    : row[col.id] === "Awaiting Documents"
                    ? theme.palette.primary.main
                    : row[col.id] === "Signed"
                    ? theme.palette.success.main
                    : row[col.id] === "Not Applicable"
                    ? "none"
                    : theme.palette.primary.main,
                color:
                  row[col.id] === "Not Applicable"
                    ? theme.palette.text.primary
                    : theme.palette.text.light,
              }}
            >
              {row[col.id] === "Not Applicable" ? "-----" : row[col.id]}
            </Box>
          </TableCell>
        );
      case "booleanBadge":
        return (
          <TableCell>
            <Box
              sx={{
                ...styles.statusBadge,
                ...finalStyles,
                bgcolor: row[col.id]
                  ? theme.palette.info.main
                  : theme.palette.primary.main,
              }}
            >
              {row[col.id] ? "Yes" : "No"}
            </Box>
          </TableCell>
        );
      case "elapsedDays":
        if (row.status !== "Closed") {
          const timeSince = getTimeSincePastDate(row.created_at);
          return (
            <TableCell>
              <b>{timeSince.days}</b> days <b>{timeSince.hours}</b> hours
            </TableCell>
          );
        } else {
          const timeSince = getTimeSincePastDate(
            row.created_at,
            row.elapsed_days
          );
          return (
            <TableCell>
              <b>{timeSince.days}</b> days <b>{timeSince.hours}</b> hours
            </TableCell>
          );
        }

      case "action":
        return (
          <>
            {row?.purpose_type !== purposeList.dimensional ? (
              <TableCell sx={finalStyles}>NA</TableCell>
            ) : row?.component_template_assigned &&
              row?.painting_checklist_template_assigned ? (
              <TableCell sx={finalStyles}>
                {row?.model_name || "-----"}
              </TableCell>
            ) : row?.component_template_created &&
              row?.painting_checklist_template_created ? (
              <TableCell sx={finalStyles}>
                <Tooltip
                  title={
                    !row?.component_template_assigned &&
                    !row?.painting_checklist_template_assigned
                      ? col.label
                      : !row?.component_template_assigned
                      ? "Assign Component Template"
                      : "Assign Painting Template"
                  }
                >
                  <AppButton
                    label={col.label}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssignTemplate(row);
                    }}
                    buttonType="primary"
                    sx={{ maxHeight: "30px" }}
                    isLoading={assignLoader[row.rfi_id]}
                  />
                </Tooltip>
              </TableCell>
            ) : (
              <TableCell sx={finalStyles}>
                Template Not available {"( " + row?.model_name + " )"}
              </TableCell>
            )}
          </>
        );
      default:
        return <TableCell>{row[col.id]}</TableCell>;
    }
  };

  return (
    <TableContainer
      component={Paper}
      sx={
        showAnalytics
          ? styles.expandedAnalyticTableHeight
          : styles.collapsedAnalyticTableHeight
      }
    >
      <Table
        stickyHeader
        size="small"
        aria-label="project dashboard records table"
      >
        <TableHead>
          <TableRow hover key="header" sx={styles.tableRow}>
            {columns.map((col) => (
              <TableCell
                key={col.id}
                sx={styles.tableHeaderCell}
                sortDirection={sortBy === col.id ? sortDirection : false}
              >
                <span style={styles.headerSpan}>
                  {col.sortable !== false ? (
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortDirection : "asc"}
                      onClick={() => handleSort(col.id)}
                      sx={styles.tableSortLabel}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </span>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    mt: 6,
                  }}
                >
                  <CircularProgress />
                </Box>
              </TableCell>
            </TableRow>
          ) : tableData?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ mt: 5, color: theme.palette.primary.main }}
                >
                  No Data Found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            tableData.map((row, idx) => (
              <TableRow
                key={idx}
                onClick={() => handleRowClick(row)}
                sx={styles.bodyRow(idx)}
                hover
              >
                {columns.map((col) => renderCell(col, row))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProjectTable;
